import React, { Fragment, useEffect, useState } from "react";
import { Form, Input, Button, Card, Row, Col, message, Space } from "antd";
import { useNavigate } from "react-router-dom";
import styles from "./login.module.scss";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "store/hook";
import { setStateCid, setIsLogin, setLevel, setLoginCid, setUser, setGid } from "../store/globalSlice";
import { CharacterVerify } from "../../Utility/RegExp";
import { useLogin } from "./quires";
import GraphicValidator from "./GraphicValidation";
import { useUpdateUserSetting, useUpdateSuperSetting, getSetting } from "Components/Header/queries";
import LoginLogo from "image/LoginLogo.png";

interface formValueProps {
  name: string;
  password: string;
  verifyCode: string;
}

const Login = () => {
  const { level, logPath, stateCid } = useAppSelector((state) => state.global);
  const disp = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // mutateAsync用於手動觸發異步操作。
  const { mutateAsync: loginMutation, isLoading: loginIsLoading } = useLogin();
  const [verifyCode, setVerifyCode] = useState("0000");
  const { mutate: updateUserSettingMutation } = useUpdateUserSetting();
  const { mutate: updateSuperSettingMutation } = useUpdateSuperSetting();

  useEffect(() => {
    disp(setIsLogin(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // 在頁面渲染將 產生一次隨機驗證碼 到 VerifyCode
    generateVerifyCode();
  }, []);
  // 產生驗證碼 VerifyCode = 隨機驗證碼
  const generateVerifyCode = () => {
    const result = [];
    for (let i = 0; i < 4; i++) {
      const ranNum = Math.ceil(Math.random() * 25); // 0~25隨機數字
      // 大寫字母'A'的ASCII是65,A~Z的ASCII碼就是65 + 0~25;然後調用String.fromCharCode()傳入ASCII值返回相應的字符並push進Array裡
      result.push(String.fromCharCode(65 + ranNum));
    }
    setVerifyCode(result.join(""));
  };

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const refreshSuperToken = async (name: string, level: string, maxRetries = 3): Promise<string | null> => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await sleep(500); // 每次重試前等待 1 秒
        const settingRes = await getSetting(name, level);
        const newToken = settingRes?.openapi_token;

        if (newToken) {
          return newToken; // 成功取得 token
        } else {
          console.warn(`Attempt ${attempt}: No token returned from getSetting`);
        }
      } catch (err) {
        console.error(`Attempt ${attempt} failed to refresh super_super token`, err);
      }
    }

    console.error("Failed to refresh super_super token after 3 attempts");
    return null; // 重試 3 次失敗
  };
  /* 在使用者點擊登錄按鈕時被觸發，首先檢查驗證碼是否正確。
     使用了 loginMutation 這個 Query Hook 提供的 mutateAsync 函數，手動觸發登錄相關的異步操作。
     根據登錄結果更新 Redux 狀態，並執行相應的後續操作，如導向不同頁面、更新用戶設置等。*/

  const userLogin = async (values: formValueProps) => {
    if (values.verifyCode?.toLocaleUpperCase() !== verifyCode && process.env.NODE_ENV === "production") {
      message.error(t("ISMS.Captcha Error"));
      generateVerifyCode();
      return;
    }

    try {
      const res = await loginMutation({ values });
      const userData = res.data.response;

      // 儲存基本登入資訊
      let refreshedToken: string | null = null;

      // super_super 且沒有 token，先刷新 token
      if (userData.level === "super_super" && !userData.openapi_token) {
        refreshedToken = await refreshSuperToken(userData.name, userData.level); // 回傳 token
        if (!refreshedToken) {
          message.error(t("ISMS.OpenAPI Token request failed"));
          generateVerifyCode();
          return; // 不繼續登入流程
        }
      }
      // 使用暫存 token，整合到 userData
      const finalToken =
        userData.level === "super_super"
          ? refreshedToken || userData.openapi_token || ""
          : userData.openapi_token || "";

      // **如果是 super_super 且沒有 token，就直接呼叫 API 生成**

      message.success(t("ISMS.Login"));
      localStorage.setItem("authUser.name", userData.name);
      localStorage.setItem("authUser.cid", userData.cid);
      localStorage.setItem("authUser.level", userData.level);
      localStorage.setItem("authUser.openapiToken", finalToken);
      disp(setUser(userData.name));
      disp(setLevel(userData.level));
      disp(setLoginCid(userData.cid));
      disp(setStateCid(""));
      disp(setIsLogin(true));

      // 其他登入後邏輯
      if (userData.level === "super_super") {
        updateSuperSettingMutation({ isLock: false });
      } else {
        updateUserSettingMutation({ user: userData.name, isLock: false });
        disp(setGid(userData.gid));
      }

      // 導頁
      if (logPath && logPath !== "/userlogin") {
        navigate(logPath);
      } else {
        navigate("/");
      }
    } catch (error: any) {
      if (error.response) {
        message.error(t("ISMS.Name or Password Error"));
        generateVerifyCode();
      }
      console.error(error);
      navigate("/userlogin");
    }
  };
  // #F002FA7 #FF4500
  return (
    <div className={styles.login}>
      <Card style={{ borderRadius: "10px", boxShadow: "0.5px 0.5px 16px lightgray" }}>
        <img src={LoginLogo} className={styles.login__logo} alt="" />
        <Form onFinish={userLogin} layout="vertical">
          {/* <Space direction="vertical"> */}
          {/* 帳號Form.Item */}
          <Row gutter={24} justify="center" align="middle">
            <Col
              xs={{ span: 23 }}
              sm={{ span: 23 }}
              md={{ span: 23 }}
              lg={{ span: 23 }}
              xl={{ span: 23 }}
              xxl={{ span: 23 }}
            >
              <Form.Item
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Username is required!",
                  },
                  CharacterVerify,
                ]}
              >
                <Input prefix={<UserOutlined />} placeholder="Username" maxLength={30} data-testid="username" />
              </Form.Item>
            </Col>
          </Row>
          {/* 密碼Form.Item */}
          <Row gutter={24} justify="center">
            <Col
              xs={{ span: 23 }}
              sm={{ span: 23 }}
              md={{ span: 23 }}
              lg={{ span: 23 }}
              xl={{ span: 23 }}
              xxl={{ span: 23 }}
            >
              <Form.Item
                name="password"
                rules={[
                  {
                    required: true,
                    message: "Password is required!",
                  },
                  CharacterVerify,
                ]}
              >
                <Input.Password
                  autoSave="off"
                  autoComplete="new-password"
                  maxLength={30}
                  placeholder="Password"
                  prefix={<LockOutlined />}
                  data-testid="password"
                />
              </Form.Item>
            </Col>
          </Row>
          {/* process.env.NODE_ENV 值為 production 才會顯示驗證碼 */}
          {process.env.NODE_ENV === "production" && (
            <Row gutter={24} justify="center" align="middle">
              <Col
                xs={{ span: 23 }}
                sm={{ span: 23 }}
                md={{ span: 23 }}
                lg={{ span: 23 }}
                xl={{ span: 12 }}
                xxl={{ span: 12 }}
              >
                {/* 驗證碼Form.Item */}
                <Form.Item name="verifyCode" rules={[{ required: true, message: "CAPTCHA required" }]}>
                  <Input autoComplete="off" maxLength={4} placeholder="Enter Captcha" />
                </Form.Item>
              </Col>
              <Col
                xs={{ span: 23 }}
                sm={{ span: 23 }}
                md={{ span: 23 }}
                lg={{ span: 23 }}
                xl={{ span: 11 }}
                xxl={{ span: 11 }}
              >
                <Form.Item>
                  <GraphicValidator generateVerifyCode={generateVerifyCode} verifyCode={verifyCode} />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={24} justify="center">
            <Col
              xs={{ span: 23 }}
              sm={{ span: 23 }}
              md={{ span: 23 }}
              lg={{ span: 23 }}
              xl={{ span: 23 }}
              xxl={{ span: 23 }}
            >
              <Form.Item>
                <Button block type="primary" htmlType="submit" loading={loginIsLoading} data-testid="login">
                  {t("ISMS.Login")}
                </Button>
              </Form.Item>
            </Col>
          </Row>
          {/* </Space> */}
        </Form>
      </Card>
    </div>
  );
};

export default Login;
